import { useEffect, useState, useContext } from "react";
import ListAndAddTabs from "components/Layout/ListAndAddTabs";
import TasksTable from "Modules/Tasks/TasksTable";
import Page from "components/Page";
import StepperFrame, {
  InvalidationCheck,
  StepFrame,
} from "components/Layout/StepperFrame";
import { GET_FLOOR_OVERLAYS } from "lib/buildings";
import parser from "cron-parser";
import {
  BarcodeTaskItem_Insert_Input,
  ChecklistItem_Insert_Input,
  Checklist_Insert_Input,
  FormItem_Insert_Input,
  FormListItem_Insert_Input,
  GetFloorOverlaysQueryResult,
  InsertNextRunTimeMutationVariables,
  InsertTaskMutationResult,
  InsertTaskMutationVariables,
  OnceOffSchedule_Insert_Input,
  RoleTaskSend_Insert_Input,
  RoleTaskStart_Insert_Input,
  Schedules_Insert_Input,
  SignatureTaskItem_Insert_Input,
  SubTask_Insert_Input,
  TakePhotoTaskItem_Insert_Input,
  TaskItem_Insert_Input,
} from "generated/graphql";
import { useMutation, useQuery } from "@apollo/client";
import StepsCreator, { StepInput } from "Modules/Tasks/StepsCreator";
import BasicTaskConfig from "Modules/Tasks/BasicTaskConfig";
import ScheduleCreator, {
  OnceOffInput,
  ScheduleInput,
} from "Modules/Tasks/ScheduleCreator";
import { BuildingContext } from "Context/BuildingProvider";
import TaskRoleChecklists from "Modules/Tasks/TaskRoleChecklists";
import { GET_TASKS, INSERT_NEXT_RUN, INSERT_TASK } from "lib/tasks";
import { TaskItemTypeEnum } from "Modules/Tasks/TaskItemListAndAdd";
import { AuthContext } from "Firebase/AuthProvider";
import { LinearProgress } from "@material-ui/core";

export enum SubTaskTypeEnum {
  WAYPOINT = "Waypoint",
  TASK_ITEM_LIST = "TaskItemList",
}
export interface TaskInput {
  name: string;
  priority: "1" | "2" | "3" | "4";
  steps: StepInput[];
  schedules: ScheduleInput[];
  onceOffs: OnceOffInput[];
  selectedRoleTaskStarts: number[];
  selectedRoleTaskSends: number[];
}
interface TasksState {
  editing: boolean;
  tabValue: number;
  currentTask: TaskInput;
  loading: Boolean;
}

const DEFAULT_STATE: TasksState = {
  editing: false,
  tabValue: 0,
  loading: false,
  currentTask: {
    name: "",
    priority: "3",
    steps: [],
    schedules: [],
    onceOffs: [],
    selectedRoleTaskSends: [],
    selectedRoleTaskStarts: [],
  },
};

export default function Tasks() {
  const [state, setState] = useState(DEFAULT_STATE);
  const { currentBuilding, updateBuilding } = useContext(BuildingContext);
  const { setRoute } = useContext(AuthContext);
  useEffect(() => {
    setRoute("Tasks");
  }, []);
  const {
    loading: getFloorsLoading,
    error,
    data,
  }: GetFloorOverlaysQueryResult = useQuery(GET_FLOOR_OVERLAYS, {
    variables: {
      buildingId: currentBuilding?.Id,
    },
  });

  const [insertTask] = useMutation<InsertTaskMutationVariables>(INSERT_TASK, {
    refetchQueries: [{ query: GET_TASKS }],
  });
  const [insertNextRun] =
    useMutation<InsertNextRunTimeMutationVariables>(INSERT_NEXT_RUN);

  const list: JSX.Element = <TasksTable />;

  const handleTaskNameChange = (name: string) => {
    setState((prevState) => ({
      ...prevState,
      currentTask: { ...prevState.currentTask, name },
    }));
  };
  const handleTaskPriorityChange = (priority: "1" | "2" | "3" | "4") => {
    setState((prevState) => ({
      ...prevState,
      currentTask: { ...prevState.currentTask, priority },
    }));
  };

  const submitStep = (step: StepInput) => {
    const steps = state.currentTask.steps;
    steps.push(step);
    setState((prevState) => ({
      ...prevState,
      currentStep: { ...prevState.currentTask, steps },
    }));
  };

  const onOnceOffSubmit = (onceOff: OnceOffInput) => {
    const onceOffs = state.currentTask.onceOffs;
    onceOffs.push(onceOff);
    setState((prevState) => ({
      ...prevState,
      currentTask: { ...prevState.currentTask, onceOffs },
    }));
  };

  const onScheduleSubmit = (schedule: ScheduleInput) => {
    const schedules = state.currentTask.schedules;
    schedules.push(schedule);
    console.log(
      "🚀 ~ file: tasks.tsx ~ line 133 ~ onScheduleSubmit ~ schedules",
      schedules
    );
    setState((prevState) => ({
      ...prevState,
      currentTask: { ...prevState.currentTask, schedules },
    }));
  };

  const handleTaskSendClick = (id: string | number) => {
    if (typeof id === "string") {
      id = parseInt(id);
    }
    if (state.currentTask.selectedRoleTaskSends.includes(id)) {
      const newRoleTasks = state.currentTask.selectedRoleTaskSends.filter(
        (rts) => {
          return rts !== id;
        }
      );
      setState((prevState) => ({
        ...prevState,
        currentTask: {
          ...prevState.currentTask,
          selectedRoleTaskSends: newRoleTasks,
        },
      }));
    } else {
      const newRoleTasks = state.currentTask.selectedRoleTaskSends;
      newRoleTasks.push(id);
      setState((prevState) => ({
        ...prevState,
        currentTask: {
          ...prevState.currentTask,
          selectedRoleTaskSends: newRoleTasks,
        },
      }));
    }
  };

  const handleTaskStartClick = (id: string | number) => {
    if (typeof id === "string") {
      id = parseInt(id);
    }
    if (state.currentTask.selectedRoleTaskStarts.includes(id)) {
      const newRoleTasks = state.currentTask.selectedRoleTaskStarts.filter(
        (rts) => {
          return rts !== id;
        }
      );
      setState((prevState) => ({
        ...prevState,
        currentTask: {
          ...prevState.currentTask,
          selectedRoleTaskStarts: newRoleTasks,
        },
      }));
    } else {
      const newRoleTasks = state.currentTask.selectedRoleTaskStarts;
      newRoleTasks.push(id);
      setState((prevState) => ({
        ...prevState,
        currentTask: {
          ...prevState.currentTask,
          selectedRoleTaskStarts: newRoleTasks,
        },
      }));
    }
  };

  const checkIfTaskConfigIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (state.currentTask.name === "") {
      invalid = true;
      message = "Please add a name to the task";
    }
    return { invalid, message };
  };

  const loading = getFloorsLoading || state.loading;
  const checkIfStepsCreationConfigIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (state.currentTask.steps.length === 0) {
      invalid = true;
      message = "Please add steps to the task";
    }
    return { invalid, message };
  };
  const stepFrames: StepFrame[] = [
    {
      label: "Name and Configure Task *",
      children: (
        <BasicTaskConfig
          handleTaskPriorityChange={handleTaskPriorityChange}
          handleTaskNameChange={handleTaskNameChange}
        />
      ),
      inValid: checkIfTaskConfigIsInvalid(),
    },
    {
      label: "Choose a Schedule (Optional)",
      children: (
        <ScheduleCreator
          schedules={state.currentTask.schedules}
          onceOffs={state.currentTask.onceOffs}
          onScheduleSubmit={onScheduleSubmit}
          onOnceOffSubmit={onOnceOffSubmit}
        />
      ),
    },
    {
      label: "Add Steps",
      inValid: checkIfStepsCreationConfigIsInvalid(),
      children: loading ? (
        <div>loading</div>
      ) : error ? (
        <div>{error.message}</div>
      ) : (
        <StepsCreator
          steps={state.currentTask.steps}
          submitStep={submitStep}
        ></StepsCreator>
      ),
    },
    {
      label: "Assign Roles",
      children: (
        <TaskRoleChecklists
          handleTaskStartClick={handleTaskStartClick}
          handleTaskSendClick={handleTaskSendClick}
          selectedRoleTaskStarts={state.currentTask.selectedRoleTaskStarts}
          selectedRoleTaskSends={state.currentTask.selectedRoleTaskSends}
        />
      ),
    },
  ];

  const handleTabChange = (newValue: number) => {
    setState({
      ...state,
      tabValue: newValue,
      currentTask: {
        name: "",
        priority: "3",
        steps: [],
        schedules: [],
        onceOffs: [],
        selectedRoleTaskSends: [],
        selectedRoleTaskStarts: [],
      },
    });
  };

  const removeLeadingZero = (time: string) => {
    let returnNumber;
    if (time.substring(0, 1) === "0") {
      returnNumber = time.substring(1, 2);
    } else {
      returnNumber = time;
    }

    return returnNumber;
  };

  const createCronForOnceOff = (onceOff: OnceOffInput): string => {
    const firstArray = onceOff.dateTime.split("T");
    const dateArray = firstArray[0].split("-");
    const day = dateArray[2];
    const month = dateArray[1];
    const timeArray = firstArray[1].split(":");
    const min = timeArray[1];
    const hour = timeArray[0];
    const cronSchedule =
      "0 " + min + " " + hour + " " + day + " " + month + " ?";
    return cronSchedule;
  };
  const createCronForSchedule = (schedule: ScheduleInput): string => {
    let daysCronPretty = "";
    if (schedule.days.length > 1) {
      let daysCronUgly = "";

      schedule.days.forEach((day) => {
        const shortStr = day.slice(0, 3);
        //console.log(shortStr);
        daysCronUgly = daysCronUgly + "," + shortStr;
      });
      daysCronPretty = daysCronUgly.substr(1);
    } else if (schedule.days.length === 1) {
      daysCronPretty = schedule.days[0].slice(0, 3);
    }
    let timeHour = schedule.time.substring(0, 2);
    timeHour = timeHour.replace(":", "");
    timeHour = removeLeadingZero(timeHour);
    var timeMinute = removeLeadingZero(schedule.time.slice(-2));
    const cronSchedule =
      timeMinute +
      " " +
      timeHour +
      " " +
      "*" +
      " " +
      "*" +
      " " +
      daysCronPretty;
    return cronSchedule;
  };

  const onSubmit = async () => {
    //post task
    setState({ ...state, loading: true });
    const schedules: Schedules_Insert_Input[] = [];
    state.currentTask.schedules.forEach((schedule) => {
      const cronExpression = createCronForSchedule(schedule);
      schedules.push({
        days: schedule.days,
        time: schedule.time,
        cronExpression,
      });
    });
    console.log(
      "🚀 ~ file: tasks.tsx ~ line 308 ~ onSubmit ~ schedules",
      schedules
    );

    const onceOffs: OnceOffSchedule_Insert_Input[] = [];
    state.currentTask.onceOffs.forEach((onceOff) => {
      const cronExpression = createCronForOnceOff(onceOff);
      onceOffs.push({
        dateTime: onceOff.dateTime,
        cronExpression,
      });
    });
    const subTasks: SubTask_Insert_Input[] = [];
    let orderNumber = 1;
    state.currentTask.steps.forEach((step) => {
      if (step.stepLocation) {
        const waypoint = {
          Lat: step.stepLocation.lat,
          Long: step.stepLocation.lng,
          floorNumber: step.stepLocation.floorNumber,
        };

        subTasks.push({
          Description: step.name,
          Waypoints: { data: [waypoint] },
          OrderNumber: orderNumber,
          SubTaskType: SubTaskTypeEnum.WAYPOINT,
        });
        orderNumber++;
      }

      const taskItems: TaskItem_Insert_Input[] = [];
      let taskItemOrderNumber = 1;
      step.taskItems.forEach((taskItem) => {
        const barcodes: BarcodeTaskItem_Insert_Input[] = [];
        const checklists: Checklist_Insert_Input[] = [];
        const forms: FormItem_Insert_Input[] = [];
        const signatures: SignatureTaskItem_Insert_Input[] = [];
        const takePhotos: TakePhotoTaskItem_Insert_Input[] = [];
        switch (taskItem.type) {
          case TaskItemTypeEnum.BARCODE:
            barcodes.push({
              Title: taskItem.name,
            });
            break;
          case TaskItemTypeEnum.CHECKLIST:
            const checklistItems: ChecklistItem_Insert_Input[] = [];
            taskItem.options.forEach((option) => {
              checklistItems.push({
                Title: option.label,
              });
            });
            checklists.push({
              Title: taskItem.name,
              ChecklistItems: { data: checklistItems },
            });
            break;
          case TaskItemTypeEnum.DROPDOWN:
            const items: FormListItem_Insert_Input[] = [];
            taskItem.options.forEach((option) => {
              items.push({
                ItemName: option.label,
              });
            });
            forms.push({
              Title: taskItem.name,
              FromList: true,
              FormListItems: { data: items },
            });
            break;
          case TaskItemTypeEnum.FORM:
            forms.push({
              Title: taskItem.name,
              FromList: false,
            });
            break;
          case TaskItemTypeEnum.SIGNATURE:
            signatures.push({
              Title: taskItem.name,
            });
            break;
          case TaskItemTypeEnum.TAKE_PHOTO:
            takePhotos.push({
              Title: taskItem.name,
            });
            break;
        }
        taskItems.push({
          OrderNumber: taskItemOrderNumber,
          BarcodeTaskItems: { data: barcodes },
          Checklists: { data: checklists },
          FormItems: { data: forms },
          SignatureTaskItems: { data: signatures },
          TakePhotoTaskItems: { data: takePhotos },
          TaskItemType:
            taskItem.type === TaskItemTypeEnum.DROPDOWN
              ? TaskItemTypeEnum.FORM
              : taskItem.type,
        });
        taskItemOrderNumber++;
      });
      subTasks.push({
        Description: step.name,
        OrderNumber: orderNumber,
        SubTaskType: SubTaskTypeEnum.TASK_ITEM_LIST,
        TaskItems: { data: taskItems },
      });
      orderNumber++;
    });

    const roleTaskSends: RoleTaskSend_Insert_Input[] = [];
    state.currentTask.selectedRoleTaskSends.forEach((rts) => {
      roleTaskSends.push({
        RoleId: rts,
      });
    });
    const roleTaskStarts: RoleTaskStart_Insert_Input[] = [];
    state.currentTask.selectedRoleTaskStarts.forEach((rts) => {
      roleTaskStarts.push({
        RoleId: rts,
      });
    });
    try {
      //@ts-ignore
      const result: InsertTaskMutationResult = await insertTask({
        variables: {
          Name: state.currentTask.name,
          BuildingId: currentBuilding?.Id,
          Priority: state.currentTask.priority,
          SubTasks: { data: subTasks },
          Schedules: { data: schedules },
          OnceOffSchedules: { data: onceOffs },
          RoleTaskSends: { data: roleTaskSends },
          RoleTaskStarts: { data: roleTaskStarts },
        },
      });
      if (result.data?.insert_Task?.returning[0].Schedules) {
        for (
          let i = 0;
          i < result.data?.insert_Task?.returning[0].Schedules.length;
          i++
        ) {
          const schedule = result.data?.insert_Task?.returning[0].Schedules[i];

          if (schedule.cronExpression) {
            let interval = parser.parseExpression(schedule.cronExpression);
            let variables = {
              cronSchedule: schedule.cronExpression,
              nextRunTime: interval.next(),
              taskName: result.data?.insert_Task?.returning[0].Name,
              priority: result.data?.insert_Task?.returning[0].Priority,
              buildingId: currentBuilding?.Id,
              roleId: "Any",
              taskId: result.data?.insert_Task?.returning[0].Id,
              scheduleId: schedule.Id,
              onceOff: false,
            };
            await insertNextRun({
              variables,
            });
          }
        }
      }
      if (result.data?.insert_Task?.returning[0].OnceOffSchedules) {
        for (
          let i = 0;
          i < result.data?.insert_Task?.returning[0].OnceOffSchedules?.length;
          i++
        ) {
          const onceoff =
            result.data?.insert_Task?.returning[0].OnceOffSchedules[i];

          if (onceoff.cronExpression) {
            let interval = parser.parseExpression(onceoff.cronExpression);
            let variables = {
              cronSchedule: onceoff.cronExpression,
              nextRunTime: interval.next(),
              taskName: result.data?.insert_Task?.returning[0].Name,
              priority: result.data?.insert_Task?.returning[0].Priority,
              buildingId: currentBuilding?.Id,
              roleId: "Any",
              taskId: result.data?.insert_Task?.returning[0].Id,
              onceOff: true,
            };
            await insertNextRun({
              variables,
            });
          }
        }
      }
      setState({
        ...state,
        tabValue: 0,
        loading: false,
        currentTask: {
          name: "",
          priority: "3",
          steps: [],
          schedules: [],
          onceOffs: [],
          selectedRoleTaskSends: [],
          selectedRoleTaskStarts: [],
        },
      });
    } catch (e) {
      setState({ ...state, loading: false });
      console.log("🚀 ~ file: tasks.tsx ~ line 437 ~ onSubmit ~ e", e);
    }
  };

  const add: JSX.Element = (
    <StepperFrame stepFrames={stepFrames} handleSubmit={onSubmit} />
  );

  if (loading) {
    return <LinearProgress />;
  } else if (error) {
    return <div>{error.message}</div>;
  } else {
    return (
      <Page title="Tasks">
        <ListAndAddTabs
          value={state.tabValue}
          handleChange={handleTabChange}
          listChildren={list}
          addChildren={add}
          fromEdit={state.editing}
        />
      </Page>
    );
  }
}
